import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { ClientAction, ClientEvent, Party, PlayerView, PublicView } from '@sh/shared'
import { BoardPanel } from '../components/BoardPanel.js'
import { Cinematic } from '../components/Cinematic.js'
import { HoldHintProvider } from '../components/HoldHint.js'
import { AppMark } from '../components/Icons.js'
import { trackEvent } from '../net/analytics.js'
import { useFinePointer } from '../net/useDevice.js'
import { EVENT_MS } from '../net/useGameEvents.js'
import type { EventBody, GameEvent } from '../net/useGameEvents.js'
import { DefaultSkinProvider } from '../skin/SkinContext.js'
import { useTourStop } from '../tour/Tour.js'
import type { Stop } from '../tour/Tour.js'
import { ExecutivePhase } from './phases/Executive.js'
import { NominationPhase, VotingPhase } from './phases/Election.js'
import { PresidentPhase } from './phases/Legislative.js'
import { RevealPhase } from './phases/Reveal.js'
import type { Tour } from '../tour/script.js'

/**
 * /learn - a guided tour of the game, for somebody who has nobody to play with
 * yet or does not know the game.
 *
 * A visitor arriving alone has nothing to do on the home page but read it, and
 * the game is the kind that does not explain itself in a paragraph. This walks
 * them through one seven-seat game on the real screens a phone shows at a
 * table, fed by the real engine, with the six other players scripted: a secret
 * role, a vote, a turn as President, a Chancellor who lies about it, a power,
 * and how each side wins.
 *
 * Where the real screen has a move, the tour waits for it; the rest has Next.
 * Everything runs in the browser and nothing is sent anywhere. The reducer and
 * the overlay library only load once somebody starts, so the page itself stays
 * as light as every other.
 */

export type Step =
  | 'intro'
  | 'role'
  | 'teams'
  | 'government'
  | 'vote'
  | 'track'
  | 'tracker'
  | 'nominate'
  | 'discard'
  | 'betrayal'
  | 'power'
  | 'result'
  | 'liberal'
  | 'fascist'
  | 'end'

/** Everything a stop needs, as it stands at that point in the game. */
export interface Snap {
  tour: Tour
  /** What your phone shows. */
  phone: PlayerView
  /** What the table's shared board shows. */
  board: PublicView
  /**
   * You tried to throw away the Liberal. The tour does not take it: a Liberal
   * keeps the Liberal, and the betrayal the next stop is about needs one in
   * the Chancellor's hand to lie about.
   */
  refused?: boolean
  /** Who you investigated, once you have. */
  investigated?: string
  party?: Party
}

/** Stops about the shared board rather than your phone. */
const BOARD_STOPS: ReadonlySet<Step> = new Set(['track', 'tracker', 'betrayal', 'liberal', 'fascist'])

/** Where Back goes - only ever to a stop with no move between it and here. */
const BACK: Partial<Record<Step, Step>> = {
  teams: 'role',
  government: 'teams',
  vote: 'government',
  tracker: 'track',
  fascist: 'liberal',
}

/** The first stop of each chapter, and the name it is counted under. */
const CHAPTER: Partial<Record<Step, ClientEvent>> = {
  role: 'tutorial-roles',
  government: 'tutorial-election',
  nominate: 'tutorial-legislation',
  power: 'tutorial-board',
}

const nameOf = (view: PlayerView | PublicView, id: string | null | undefined) =>
  view.players.find((p) => p.id === id)?.name ?? '?'

/**
 * The note for each stop. Plain instructions: the screens carry the mood.
 *
 * A move is committed differently on a mouse than under a thumb (see
 * `HoldToConfirm`), so `fine` picks which one the instructions describe. Telling
 * somebody at a laptop to hold a name they have to click twice left them
 * holding it, and the tour waiting for a move that never came.
 */
export function stopFor(step: Step, snap: Snap | null, fine = false): Stop | null {
  if (!snap) return null
  const { tour, phone, refused } = snap
  const tap = fine ? 'click' : 'tap'
  /** How to commit a choice, finishing the sentence `subject` starts. */
  const commit = (subject: string, verb: string) =>
    fine ? `Click ${subject}, then click it again to ${verb}.` : `Hold ${subject} until it fills to ${verb}.`
  switch (step) {
    case 'role':
      return {
        element: '.card--reveal',
        title: 'Your secret role',
        body: `Everyone at the table is dealt a secret role. Hold the envelope to read yours, then ${tap} “I have seen my role”.`,
        next: null,
      }
    case 'teams':
      return {
        element: '.learn__peek',
        title: 'Two teams, one secret',
        body: `This is ${nameOf(tour.hitlerPeek, tour.hitlerPeek.you?.id)}’s view: Hitler, who at this table size does not know who the Fascists are. Hold the card to see. You are still a Liberal. Liberals are the majority, and know nothing about anyone; the Fascists know each other and who Hitler is.`,
        next: 'Next',
        live: true,
      }
    case 'government':
      return {
        element: '.gov',
        title: 'An election',
        body: `Each round a President nominates a Chancellor. ${nameOf(phone, phone.presidentId)} is President and has picked ${nameOf(phone, phone.nomineeId)}. Before anyone votes, the table argues about it — out loud.`,
        next: 'Next',
      }
    case 'vote':
      return {
        element: '.choices--ballot',
        title: 'Vote',
        body: 'Everyone votes Ja or Nein at the same moment, and a majority of Ja elects the government. Cast yours.',
        next: null,
      }
    case 'track':
      return {
        element: '.track--fascist',
        title: 'A Fascist policy went down',
        body: 'The President drew three policies and passed two; the Chancellor enacted one. Nobody else saw the cards — so was it bad luck, or a Fascist government?',
        next: 'Next',
      }
    case 'tracker':
      return {
        element: '.tracker',
        title: 'Failed elections',
        body: 'When a vote fails, the presidency moves on and this ticks up. Three failures in a row and the top policy is enacted, whatever it is.',
        next: 'Next',
      }
    case 'nominate':
      return {
        element: `[data-testid="nominate-${tour.suspect}"]`,
        title: 'Your turn as President',
        body: `The presidency moves round the table, and now it is yours. Nominate a Chancellor: ${tour.suspect} has been very reasonable all game. ${commit('their name', 'nominate them')}`,
        next: null,
      }
    case 'discard': {
      const how = fine ? 'click it, then click it again' : 'hold it until it fills'
      return {
        element: '[data-testid="president-hand"]',
        title: refused ? 'Keep the Liberal' : 'Three policies, one discard',
        body: refused
          ? `You are a Liberal: throwing away the only Liberal policy does the Fascists’ work for them. Throw away a Fascist instead — ${how}.`
          : `You drew two Fascist policies and one Liberal. Your Chancellor gets two of them and enacts one, so as a Liberal you throw away a Fascist and pass on the Liberal. Show them, pick a Fascist, and ${how}. Nobody else will ever see these cards.`,
        next: null,
      }
    }
    case 'betrayal':
      return {
        element: '.track--fascist',
        title: `${tour.suspect} enacted a Fascist policy`,
        body: `You passed a Liberal and a Fascist. ${tour.suspect} played the Fascist, and tells the table you handed over two. You know that is a lie — but nobody can check the cards, so it is your word against theirs. That doubt is the game.`,
        next: 'Next',
      }
    case 'power':
      return {
        element: '.learn__phone .card',
        title: 'A presidential power',
        body: `Some Fascist policies hand the President a power. This one lets you secretly read one player’s party. ${tour.suspect} might be worth a look. ${commit('a name', 'investigate')}`,
        next: null,
      }
    case 'result':
      return {
        element: '.learn__phone .card',
        title: 'Only you saw that',
        body: `Hold to read the result. You can tell the table what you found, but they only have your word for it. ${fine ? 'Click' : 'Tap'} Done when you have read it.`,
        next: null,
      }
    case 'liberal':
      return {
        element: '.track--liberal',
        title: 'How the Liberals win',
        body: 'Five Liberal policies — or by executing Hitler, once the Fascist track has handed the President that power.',
        next: 'Next',
      }
    case 'fascist':
      return {
        element: '.track--fascist',
        title: 'How the Fascists win',
        body: 'Six Fascist policies — or, once three are down, by getting Hitler elected Chancellor. From then on every vote is a gamble.',
        next: 'Finish',
      }
    default:
      return null
  }
}

export function Learn() {
  const [step, setStep] = useState<Step>('intro')
  const [snap, setSnap] = useState<Snap | null>(null)
  // Scenes queued by the last move, played before the tour goes on to `after`.
  const [scenes, setScenes] = useState<EventBody[]>([])
  const after = useRef<Step | null>(null)
  // The presidency passing to you comes out of the vote, but plays later: on
  // the way into the chapter where it matters, after the board stops.
  const heldPresidency = useRef<EventBody[]>([])
  const [loading, setLoading] = useState(false)

  const go = useCallback((next: Step) => {
    const chapter = CHAPTER[next]
    if (chapter) trackEvent(chapter)
    if (next === 'end') trackEvent('tutorial-completed')
    setStep(next)
  }, [])

  const start = async () => {
    trackEvent('tutorial-started')
    setLoading(true)
    try {
      // Here rather than at the top of the file: this is the one page that
      // needs the reducer, and only once somebody has asked for the tour.
      const { buildTour } = await import('../tour/script.js')
      const tour = buildTour()
      setSnap({ tour, phone: tour.vote, board: tour.voteBoard })
      setScenes([])
      go('role')
    } finally {
      setLoading(false)
    }
  }

  function play(events: EventBody[], then: Step) {
    after.current = then
    setScenes([...events])
  }

  /*
   * The phase screens answer a move the way a phone at a table does: through
   * `act`. Here there is no table, so each move goes to the script, and the
   * tour moves on.
   */
  const act = (action: ClientAction) => {
    if (!snap) return
    const { tour } = snap
    switch (action.type) {
      case 'ack_reveal':
        if (step === 'role') go('teams')
        return
      case 'cast_ballot': {
        const out = tour.cast(action.ja)
        setSnap({ ...snap, phone: out.nominate, board: out.board })
        // The election and the policy, then the board. The presidency passing
        // to you plays later, as the chapter where it matters opens.
        heldPresidency.current = out.events.filter((e) => e.kind === 'presidency')
        play(out.events.filter((e) => e.kind !== 'presidency'), 'track')
        return
      }
      case 'nominate': {
        const out = tour.nominate(action.nomineeId)
        setSnap({ ...snap, phone: out.hand, board: out.board })
        play(out.events, 'discard')
        return
      }
      case 'president_discard': {
        if (snap.phone.presidentHand?.[action.index] === 'liberal') {
          setSnap({ ...snap, refused: true })
          return
        }
        const out = tour.discard(action.index)
        setSnap({ ...snap, phone: out.power, board: out.board, refused: false })
        play(out.events, 'betrayal')
        return
      }
      case 'power_investigate': {
        const out = tour.investigate(action.targetId)
        setSnap({ ...snap, phone: out.view, investigated: action.targetId, party: out.party })
        go('result')
        return
      }
      case 'power_ack':
        go('liberal')
        return
      default:
        return
    }
  }

  const sceneDone = useCallback(() => setScenes((queue) => queue.slice(1)), [])

  // Once the last scene has played, on to the stop it was leading to.
  useEffect(() => {
    if (scenes.length > 0 || !after.current) return
    const to = after.current
    after.current = null
    go(to)
  }, [scenes, go])

  const next = () => {
    if (step === 'tracker') {
      play(heldPresidency.current, 'nominate')
      heldPresidency.current = []
      return
    }
    const order: Partial<Record<Step, Step>> = {
      teams: 'government',
      government: 'vote',
      track: 'tracker',
      betrayal: 'power',
      liberal: 'fascist',
      fascist: 'end',
    }
    const to = order[step]
    if (to) go(to)
  }

  return (
    <DefaultSkinProvider>
      <HoldHintProvider>
        <TourView
          step={step}
          snap={snap}
          scenes={scenes}
          loading={loading}
          onStart={() => void start()}
          act={act}
          onSceneDone={sceneDone}
          onNext={next}
          onBack={BACK[step] ? () => go(BACK[step] as Step) : undefined}
          onSkip={() => {
            after.current = null
            setScenes([])
            go('end')
          }}
        />
      </HoldHintProvider>
    </DefaultSkinProvider>
  )
}

/**
 * The page for one stop, given everything it needs. Separate from `Learn` so a
 * story can put any stop on screen without playing its way there.
 */
export function TourView({
  step,
  snap,
  scenes = [],
  loading = false,
  onStart,
  act,
  onSceneDone,
  onNext,
  onBack,
  onSkip,
}: {
  step: Step
  snap: Snap | null
  scenes?: EventBody[]
  loading?: boolean
  onStart: () => void
  act: (action: ClientAction) => void
  onSceneDone: () => void
  onNext: () => void
  onBack?: () => void
  onSkip: () => void
}) {
  const playing = scenes.length > 0
  const fine = useFinePointer()
  const stop = useMemo(() => (playing ? null : stopFor(step, snap, fine)), [playing, step, snap, fine])
  useTourStop(stop, { onNext, onBack, onSkip })

  // The tour proper: your phone, and the table's board beside it.
  const touring = step !== 'intro' && step !== 'end' && snap

  return (
    <main className={`shell learn ${touring ? 'is-touring' : ''}`} data-testid="learn" data-step={step}>
      <header className="shell__head">
        <Link className="shell__mark" to="/" aria-label="Secret Hitler home">
          <AppMark />
        </Link>
        <span className="learn__label">The two-minute tour</span>
      </header>

      {step === 'intro' && (
        <div className="shell__body">
          <section className="card" data-testid="learn-intro">
            <h1 className="card__title">Learn to play in two minutes</h1>
            <p className="card__hint">
              A guided tour of one game at a table of seven, on the same screens your phone shows at
              a real one. You get a secret role, vote, take a turn as President, and see how each side
              wins. Nothing to sign up for, and nothing is sent anywhere.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={onStart}
              disabled={loading}
              data-testid="learn-start"
            >
              {loading ? 'one moment…' : 'Start the tour'}
            </button>
          </section>
        </div>
      )}

      {touring && (
        <div className={`learn__table ${BOARD_STOPS.has(step) ? 'is-board-stop' : ''}`}>
          <Column
            className="learn__phone"
            caption={step === 'teams' ? `${peekName(snap)}’s view, not yours` : 'Your phone'}
          >
            <PhoneScreen step={step} snap={snap} act={act} />
          </Column>
          <Column className="learn__board" caption="The table’s shared board">
            <BoardPanel view={snap.board} />
          </Column>
        </div>
      )}

      {step === 'end' && (
        <div className="shell__body">
          <section className="card" data-testid="learn-end">
            <h2 className="card__title">That is the game</h2>
            <p className="card__hint">
              Secret roles, open votes, hidden cards, and a lot of talking. A full game is 5–10
              people around one table, about thirty minutes, everyone on their own phone.
            </p>
            <Link
              className="btn btn--primary btn--block"
              to="/"
              onClick={() => trackEvent('tutorial-to-create')}
              data-testid="learn-create"
            >
              Start a table
            </Link>
            <Link
              className="btn btn--block"
              to="/?mode=practice"
              onClick={() => trackEvent('tutorial-to-practice')}
              data-testid="learn-practice"
            >
              Practice against bots
            </Link>
            <p className="home__hint">
              <Link to="/rules">Read the full rules</Link>
            </p>
          </section>
        </div>
      )}

      {playing && <Scenes events={scenes} onAdvance={onSceneDone} />}
    </main>
  )
}

function Column({ className, caption, children }: { className: string; caption: string; children: ReactNode }) {
  return (
    <div className={`learn__col ${className}`}>
      <p className="learn__caption">{caption}</p>
      {children}
    </div>
  )
}

/** The player whose view the teams stop borrows: Hitler. */
const peekName = (snap: Snap) => nameOf(snap.tour.hitlerPeek, snap.tour.hitlerPeek.you?.id)

/** The screen your phone would be on at this stop. */
function PhoneScreen({ step, snap, act }: { step: Step; snap: Snap; act: (a: ClientAction) => void }) {
  const { phone, tour } = snap
  switch (step) {
    case 'role':
      return <RevealPhase view={tour.reveal} act={act} />
    case 'teams':
      // Somebody else's screen, and it has to look it: the card under it says
      // "your secret role", and a first-timer who has just read Liberal there
      // can take Hitler's card as their role changing. The slip says whose it
      // is, and the reveal's own button and count - which would be asking this
      // visitor to confirm a role that is not theirs - are not shown.
      return (
        <div className="learn__peek">
          <p className="learn__peek-slip">
            <strong>Not your card.</strong> You are a Liberal. This is {peekName(snap)}’s view —
            they are Hitler.
          </p>
          <RevealPhase view={tour.hitlerPeek} act={() => {}} />
        </div>
      )
    case 'government':
    case 'vote':
      return <VotingPhase view={phone} act={act} />
    case 'nominate':
    case 'track':
    case 'tracker':
      return <NominationPhase view={phone} act={act} />
    case 'discard':
      return <PresidentPhase view={phone} act={act} />
    default:
      return <ExecutivePhase view={phone} act={act} />
  }
}

/** Plays the queued scenes one after another, each for as long as the game gives it. */
function Scenes({ events, onAdvance }: { events: EventBody[]; onAdvance: () => void }) {
  const body = events[0]
  // A fresh id per scene, so the cinematic replays its entrance for each one.
  const counter = useRef(0)
  const id = useMemo(() => (body ? ++counter.current : 0), [body])
  const event: GameEvent | null = body ? ({ ...body, id } as GameEvent) : null

  useEffect(() => {
    if (!body) return
    const timer = window.setTimeout(onAdvance, EVENT_MS[body.kind])
    return () => window.clearTimeout(timer)
  }, [body, onAdvance])

  return <Cinematic event={event} onSkip={onAdvance} />
}
